import { Fragment, ReactNode, useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  SunIcon,
  MoonIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useThemeStore, Theme } from "../../stores/themeStore";
import { Modal } from "../ui/Modal";
import { RoleActivation2FAModal } from "../auth/RoleActivation2FAModal";
import { hasPermission } from "../../lib/utils/permissions";
import type { AvailableRole, ActiveRoleDetails } from "../../types/auth.types";

const themeOptions: { value: Theme; label: string; icon: typeof SunIcon }[] = [
  { value: "light", label: "Light", icon: SunIcon },
  { value: "dark", label: "Dark", icon: MoonIcon },
];

export interface HeaderProps {
  tenantCode?: string;
  tenantName?: string;
  tenantLogoUrl?: string;
  userName?: string;
  userEmail?: string;
  onLogout?: () => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onBillingClick?: () => void;
  logo?: ReactNode;
  children?: ReactNode;
  /** Elements to render in the right section before the user menu (e.g., notifications) */
  rightActions?: ReactNode;
  /** Currently active role session */
  activeRole?: ActiveRoleDetails | null;
  /** Whether a role session is currently active */
  isRoleSessionActive?: boolean;
  /** Available roles that the user can activate */
  availableRoles?: AvailableRole[];
  /** Callback to activate a role */
  onActivateRole?: (tenantRoleId: string, durationHours?: number) => Promise<void>;
  /** Default role session duration from tenant settings (hours, 1-8) */
  defaultRoleSessionDurationHours?: number;
  /** Callback to deactivate the current role */
  onDeactivateRole?: () => Promise<void>;
  /** Pending 2FA state for role activation */
  pendingRoleActivation2FA?: {
    tenantRoleId: string;
    codeId: string;
    destination: string;
    expiresIn: number;
    attemptsRemaining: number;
  } | null;
  /** Callback to verify 2FA code for role activation */
  onVerifyRoleActivation2FA?: (code: string) => Promise<void>;
  /** Callback to cancel pending 2FA */
  onCancelRoleActivation2FA?: () => void;
}

/**
 * Format remaining seconds as "Xh Xm" or "Xm Xs"
 */
function formatRemainingTime(seconds: number): string {
  if (seconds <= 0) return "Expired";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}


export const Header = ({
  tenantCode,
  tenantName,
  tenantLogoUrl,
  userName,
  userEmail,
  onLogout,
  onProfileClick,
  onSettingsClick,
  onBillingClick,
  logo,
  children,
  rightActions,
  activeRole,
  isRoleSessionActive = false,
  availableRoles = [],
  onActivateRole,
  defaultRoleSessionDurationHours,
  onDeactivateRole,
  pendingRoleActivation2FA,
  onVerifyRoleActivation2FA,
  onCancelRoleActivation2FA,
}: HeaderProps) => {
  const { theme, setTheme } = useThemeStore();
  const initials =
    userName
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  const currentThemeOption = themeOptions.find((t) => t.value === theme) || themeOptions[0];
  const ThemeIcon = currentThemeOption.icon;

  // Role session state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [selectedDurationHours, setSelectedDurationHours] = useState<number>(defaultRoleSessionDurationHours ?? 4);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [is2FALoading, setIs2FALoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState<string | null>(null);

  // Calculate remaining time from activeRole
  useEffect(() => {
    if (activeRole?.expiresAt) {
      const updateRemaining = () => {
        const expiresAt = new Date(activeRole.expiresAt).getTime();
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setRemainingTime(remaining);
      };

      updateRemaining();
      const interval = setInterval(updateRemaining, 1000);
      return () => clearInterval(interval);
    } else {
      setRemainingTime(0);
    }
  }, [activeRole?.expiresAt]);

  const handleActivateRole = useCallback(
    async (roleId: string) => {
      if (!onActivateRole) return;
      setIsLoading(true);
      setError(null);
      try {
        await onActivateRole(roleId, selectedDurationHours);
        setShowConfirmDialog(false);
        setSelectedRoleId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to activate role");
      } finally {
        setIsLoading(false);
      }
    },
    [onActivateRole, selectedDurationHours]
  );

  const handleDeactivate = useCallback(async () => {
    if (!onDeactivateRole) return;
    setIsLoading(true);
    setError(null);
    try {
      await onDeactivateRole();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to deactivate role");
    } finally {
      setIsLoading(false);
    }
  }, [onDeactivateRole]);

  const openConfirmDialog = useCallback((roleId: string) => {
    setError(null);
    setSelectedRoleId(roleId);
    setSelectedDurationHours(defaultRoleSessionDurationHours ?? 4);
    setShowConfirmDialog(true);
  }, [defaultRoleSessionDurationHours]);

  const closeConfirmDialog = useCallback(() => {
    setShowConfirmDialog(false);
    setSelectedRoleId(null);
  }, []);

  const handleVerify2FA = useCallback(
    async (code: string) => {
      if (!onVerifyRoleActivation2FA) return;
      setIs2FALoading(true);
      setTwoFAError(null);
      try {
        await onVerifyRoleActivation2FA(code);
        // Success - modal will be closed when pendingRoleActivation2FA becomes null
      } catch (err) {
        setTwoFAError(err instanceof Error ? err.message : 'Verification failed');
        throw err; // Re-throw so modal knows it failed
      } finally {
        setIs2FALoading(false);
      }
    },
    [onVerifyRoleActivation2FA]
  );

  const handleCancel2FA = useCallback(() => {
    if (onCancelRoleActivation2FA) {
      onCancelRoleActivation2FA();
    }
    setTwoFAError(null);
  }, [onCancelRoleActivation2FA]);

  const selectedRole = availableRoles.find((r) => r.roleId === selectedRoleId);
  const hasAvailableRoles = availableRoles.length > 0;

  return (
    <>
      <header
        className="border-b sticky top-0 z-40"
        style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Left section: Logo */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center">
                  {logo || (
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-primary-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">K</span>
                      </div>
                      <span className="text-xl font-bold hidden sm:block" style={{ color: 'var(--color-text-primary)' }}>Ketrics</span>
                    </div>
                  )}
                </Link>

                {(tenantName || tenantCode) && (
                  <div className="hidden md:flex items-center">
                    <span className="text-sm mx-2" style={{ color: 'var(--color-text-secondary)' }}>|</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{tenantName || tenantCode}</span>

                    {/* Role Selector Dropdown */}
                    {hasAvailableRoles && (
                      <Menu as="div" className="relative ml-2">
                        {isRoleSessionActive && activeRole ? (
                          // Active role - show role badge with timer and dropdown
                          <Menu.Button
                            className={clsx(
                              "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-colors",
                              "bg-primary-100 text-primary-800 hover:bg-primary-200",
                              "dark:bg-primary-900/30 dark:text-primary-200 dark:hover:bg-primary-900/50",
                              "focus:outline-none focus:ring-2 focus:ring-primary-500"
                            )}
                          >
                            <ShieldCheckIcon className="h-3.5 w-3.5" />
                            <span>{activeRole.roleName} ({activeRole.roleCode})</span>
                            <span className="text-primary-600 dark:text-primary-300">({formatRemainingTime(remainingTime)})</span>
                            <ChevronDownIcon className="h-3 w-3" />
                          </Menu.Button>
                        ) : (
                          // No active role - show "Activate Role" button
                          <Menu.Button
                            className={clsx(
                              "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-colors",
                              "bg-gray-100 text-gray-600 hover:bg-gray-200",
                              "dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600",
                              "focus:outline-none focus:ring-2 focus:ring-gray-500"
                            )}
                          >
                            <ShieldCheckIcon className="h-3.5 w-3.5" />
                            <span>Activate Role</span>
                            <ChevronDownIcon className="h-3 w-3" />
                          </Menu.Button>
                        )}

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items
                            className="absolute left-0 mt-2 w-56 origin-top-left rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                            style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}
                          >
                            <div className="py-1">
                              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                                {isRoleSessionActive ? "Switch Role" : "Select Role"}
                              </div>

                              {availableRoles.map((role) => (
                                <Menu.Item key={role.roleId}>
                                  {({ active }) => (
                                    <button
                                      onClick={() => openConfirmDialog(role.roleId)}
                                      disabled={isLoading}
                                      className={clsx(
                                        "flex w-full items-center gap-3 px-4 py-2 text-sm",
                                        active && "bg-primary-50 dark:bg-primary-900/20",
                                        activeRole?.roleId === role.roleId ? "text-primary-600 font-medium" : "",
                                        isLoading && "opacity-50 cursor-not-allowed"
                                      )}
                                      style={activeRole?.roleId !== role.roleId ? { color: 'var(--color-text-primary)' } : undefined}
                                    >
                                      <ShieldCheckIcon className="h-4 w-4" />
                                      <span className="flex-1 text-left">{role.roleName} ({role.roleCode})</span>
                                      {activeRole?.roleId === role.roleId && (
                                        <span className="text-xs text-primary-500">(Active)</span>
                                      )}
                                    </button>
                                  )}
                                </Menu.Item>
                              ))}

                              {isRoleSessionActive && (
                                <>
                                  <div className="my-1 border-t" style={{ borderColor: 'var(--color-border)' }} />
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={handleDeactivate}
                                        disabled={isLoading}
                                        className={clsx(
                                          "flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400",
                                          active && "bg-red-50 dark:bg-red-900/20",
                                          isLoading && "opacity-50 cursor-not-allowed"
                                        )}
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                        <span>Deactivate Role</span>
                                      </button>
                                    )}
                                  </Menu.Item>
                                </>
                              )}
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Center section: Tenant logo and custom content */}
            <div className="flex-1 flex items-center justify-center">
              {tenantLogoUrl && (
                <img
                  src={tenantLogoUrl}
                  alt={tenantName || tenantCode || "Company logo"}
                  className="h-8 max-w-[200px] object-contain"
                  onError={(e) => {
                    // Hide the image if it fails to load
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              {children}
            </div>

            {/* Right section: Theme selector and User menu */}
            <div className="flex items-center gap-2">
              {/* Theme Selector */}
              <Menu as="div" className="relative">
                <Menu.Button
                  className="p-2 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                  title={`Theme: ${currentThemeOption.label}`}
                >
                  <ThemeIcon className="h-5 w-5" />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="absolute right-0 mt-2 w-40 origin-top-right rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="py-1">
                      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Theme</div>
                      {themeOptions.map((option) => (
                        <Menu.Item key={option.value}>
                          {({ active }) => (
                            <button
                              onClick={() => setTheme(option.value)}
                              className={clsx(
                                "flex w-full items-center gap-3 px-4 py-2 text-sm",
                                active && "bg-primary-50 dark:bg-primary-900/20",
                                theme === option.value ? "text-primary-600 font-medium" : ""
                              )}
                              style={theme !== option.value ? { color: 'var(--color-text-primary)' } : undefined}
                            >
                              <option.icon className="h-4 w-4" />
                              {option.label}
                              {theme === option.value && (
                                <span className="ml-auto text-primary-600">
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </span>
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Right Actions (e.g., notifications) */}
              {rightActions}

              {/* User Menu */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium text-sm">
                    {initials}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{userName || "User"}</p>
                    {userEmail && <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{userEmail}</p>}
                  </div>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="py-1">
                      {/* User info section */}
                      {(userName || userEmail) && (
                        <>
                          <div className="px-4 py-3">
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{userName || "User"}</p>
                            {userEmail && <p className="text-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>{userEmail}</p>}
                          </div>
                          <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />
                        </>
                      )}

                      {onProfileClick && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={onProfileClick}
                              className={clsx(
                                "flex w-full items-center gap-3 px-4 py-2 text-sm",
                                active && "bg-primary-50 dark:bg-primary-900/20"
                              )}
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <UserCircleIcon className="h-5 w-5" />
                              Profile
                            </button>
                          )}
                        </Menu.Item>
                      )}

                      {onSettingsClick && hasPermission(activeRole?.permissions, 'tenant:UpdateSettings') && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={onSettingsClick}
                              className={clsx(
                                "flex w-full items-center gap-3 px-4 py-2 text-sm",
                                active && "bg-primary-50 dark:bg-primary-900/20"
                              )}
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <Cog6ToothIcon className="h-5 w-5" />
                              Settings
                            </button>
                          )}
                        </Menu.Item>
                      )}

                      {onBillingClick && hasPermission(activeRole?.permissions, 'tenant:DescribeBilling') && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={onBillingClick}
                              className={clsx(
                                "flex w-full items-center gap-3 px-4 py-2 text-sm",
                                active && "bg-primary-50 dark:bg-primary-900/20"
                              )}
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <CreditCardIcon className="h-5 w-5" />
                              Billing
                            </button>
                          )}
                        </Menu.Item>
                      )}

                      {(onProfileClick || (onSettingsClick && hasPermission(activeRole?.permissions, 'tenant:UpdateSettings')) || (onBillingClick && hasPermission(activeRole?.permissions, 'tenant:DescribeBilling'))) && onLogout && <div className="my-1 border-t" style={{ borderColor: 'var(--color-border)' }} />}

                      {onLogout && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={onLogout}
                              className={clsx(
                                "flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400",
                                active && "bg-red-50 dark:bg-red-900/20"
                              )}
                            >
                              <ArrowRightOnRectangleIcon className="h-5 w-5" />
                              Logout
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </header>

      {/* Role Activation Confirmation Dialog */}
      {hasAvailableRoles && selectedRole && (
        <Modal isOpen={showConfirmDialog} onClose={closeConfirmDialog} size="sm" closeOnOverlayClick={!isLoading}>
          <Modal.Header title={`Activate ${selectedRole.roleCode}`} />
          <Modal.Body>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                You are about to activate the <strong>{selectedRole.roleCode}</strong> role. This will grant you
                temporary elevated privileges.
              </p>

              {isRoleSessionActive && activeRole && activeRole.roleId !== selectedRole.roleId && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Your current <strong>{activeRole.roleCode}</strong> session will be deactivated.
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="role-session-duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Session duration (hours)
                </label>
                <select
                  id="role-session-duration"
                  value={selectedDurationHours}
                  onChange={(e) => setSelectedDurationHours(Number(e.target.value))}
                  disabled={isLoading}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                    <option key={h} value={h}>
                      {h} {h === 1 ? "hour" : "hours"}{h === (defaultRoleSessionDurationHours ?? 4) ? " (default)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
                <h4 className="text-sm font-medium text-primary-800 dark:text-primary-200 mb-2">This role enables access to:</h4>
                <ul className="text-sm text-primary-700 dark:text-primary-300 space-y-1 list-disc list-inside">
                  <li>Administrative functions based on role permissions</li>
                  <li>Additional sidebar navigation items</li>
                  <li>Protected management features</li>
                </ul>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your role session will automatically expire after {selectedDurationHours} {selectedDurationHours === 1 ? "hour" : "hours"}, or you can deactivate it manually at any time.
              </p>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              onClick={closeConfirmDialog}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleActivateRole(selectedRole.roleId)}
              disabled={isLoading}
              className={clsx(
                "px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500",
                "bg-primary-600 hover:bg-primary-700",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoading ? "Activating..." : `Activate ${selectedRole.roleCode}`}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Role Activation 2FA Modal */}
      {pendingRoleActivation2FA && (
        <RoleActivation2FAModal
          isOpen={!!pendingRoleActivation2FA}
          onClose={handleCancel2FA}
          destination={pendingRoleActivation2FA.destination}
          expiresIn={pendingRoleActivation2FA.expiresIn}
          attemptsRemaining={pendingRoleActivation2FA.attemptsRemaining}
          onSubmit={handleVerify2FA}
          isLoading={is2FALoading}
          error={twoFAError}
        />
      )}
    </>
  );
};
